import type { ProductsSearch } from './search-params';
import { useEffect, useRef } from 'react';

// Legacy parity (XT-658): /products filters are STICKY. Legacy kept the whole
// filter set in a localStorage-persisted store, so a search, city or supplier
// survived opening a product and coming back. The port moved filters into the
// URL — right per the state-boundary rule — but the in-app "Back"/"Cancel"
// buttons navigate to a bare `/products`, and the sidebar link does too, so
// every filter was dropped on the way back. Only `useDefaultCatalog` kept its
// legacy stickiness, which is why the catalog chip survived while the search
// box came back empty.
//
// The rule the consultant asked for: filters clear ONLY on "Reset all
// filters". That falls out of two halves —
//   - restore ONCE per mount, and only when the URL carries no filter at all
//     (a deep link or a shared URL therefore always wins, and Reset — which
//     happens after the mount decision — is never undone by a re-restore);
//   - persist on every subsequent change, so Reset's empty set is what gets
//     remembered.
//
// `catalogId` is deliberately NOT handled here: `useDefaultCatalog` already
// owns it, with its own storage key and a default-eager re-seed that this hook
// would fight. Nor are `page` / `pageSize` — a remembered page number can
// point past the end of a differently-filtered result set, and page size is a
// display preference that "Reset all filters" deliberately leaves alone.

// The sticky set. Mirrors `clearAllSearchPatch` minus catalogId/page, so
// "what Reset clears" and "what is remembered" cannot drift apart.
const STICKY_KEYS = [
  'search',
  'city',
  'supplierName',
  'contentCategory',
  'contentSubcategory',
  'country',
  'valueTier',
  'visibility',
  'cmsVisibility',
  'miniPlannerVisibility',
  'sortBy',
  'sortOrder',
  'regionId',
  'neLat',
  'neLng',
  'swLat',
  'swLng',
] as const;

type StickyKey = (typeof STICKY_KEYS)[number];
export type StickyProductFilters = Partial<Pick<ProductsSearch, StickyKey>>;

const STORAGE_KEY_PREFIX = 'products:filters';

const storageKey = (organizationId: string) =>
  `${STORAGE_KEY_PREFIX}:${organizationId}`;

/** The sticky subset of a search object, with empty values dropped. */
export const pickStickyFilters = (
  search: ProductsSearch
): StickyProductFilters => {
  const picked: Record<string, unknown> = {};
  for (const key of STICKY_KEYS) {
    const value = search[key];
    if (value === undefined || value === null || value === '') continue;
    if (Array.isArray(value) && value.length === 0) continue;
    picked[key] = value;
  }
  return picked as StickyProductFilters;
};

/**
 * Restore-time patch: every sticky key set explicitly, so keys absent from the
 * stored set are written as `undefined` and `mergeSearch` drops them. Without
 * this a leftover URL key could survive a restore that did not mention it.
 */
export const restorePatch = (
  stored: StickyProductFilters
): Record<string, unknown> => {
  const patch: Record<string, unknown> = {};
  for (const key of STICKY_KEYS) patch[key] = stored[key];
  patch.page = undefined;
  return patch;
};

const readStored = (organizationId: string): StickyProductFilters => {
  try {
    const raw = window.localStorage.getItem(storageKey(organizationId));
    if (raw === null) return {};
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return {};
    }
    // Re-validate through the same picker the write side uses, so a
    // hand-edited or stale-shape entry can never inject unknown keys.
    return pickStickyFilters(parsed as ProductsSearch);
  } catch {
    return {};
  }
};

const writeStored = (
  organizationId: string,
  filters: StickyProductFilters
): void => {
  try {
    window.localStorage.setItem(
      storageKey(organizationId),
      JSON.stringify(filters)
    );
  } catch {
    // Non-fatal: storage blocked (private mode). Filters then behave as they
    // did before — URL-only, lost on the way back.
  }
};

/**
 * Sticky, per-org product filters. `search` is the current URL search object,
 * `organizationId` the active org (also the storage key — switching orgs
 * restores that org's own filters rather than leaking another's), and
 * `onRestore` writes the remembered set back to the URL.
 */
export const useStickyProductFilters = ({
  search,
  organizationId,
  onRestore,
}: {
  search: ProductsSearch;
  organizationId: string | null | undefined;
  onRestore: (patch: Record<string, unknown>) => void;
}): void => {
  const restoredRef = useRef(false);
  const lastWrittenRef = useRef<string | null>(null);
  const onRestoreRef = useRef(onRestore);
  onRestoreRef.current = onRestore;

  useEffect(() => {
    // Need the active org before deciding — it is also the storage key.
    if (!organizationId) return;

    const current = pickStickyFilters(search);

    if (restoredRef.current) {
      // Steady state: mirror the URL into storage. Reset all filters lands
      // here with an empty set, which is what makes Reset the only thing that
      // clears the memory.
      const key = JSON.stringify(current);
      if (key === lastWrittenRef.current) return;
      lastWrittenRef.current = key;
      writeStored(organizationId, current);
      return;
    }

    restoredRef.current = true;

    // A URL that already carries filters wins outright — a deep link, a
    // shared URL or an agent-panel `apply_filters` must show exactly what it
    // says, never that plus whatever this browser happened to remember.
    if (Object.keys(current).length > 0) {
      lastWrittenRef.current = JSON.stringify(current);
      writeStored(organizationId, current);
      return;
    }

    const stored = readStored(organizationId);
    if (Object.keys(stored).length === 0) return;

    lastWrittenRef.current = JSON.stringify(stored);
    onRestoreRef.current(restorePatch(stored));
  }, [search, organizationId]);
};
